import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import qs from 'qs';
import { createSelector } from 'reselect';

import { CodeSentryFetcherName, isLinuxFetcher } from '../../../shared/fetchers';
import { plural } from '../../../shared/helpers';
import {
  Category,
  dateForFoundAfterOption,
  ENDPOINTS,
  InternalResolutionReasonDescriptions,
  InternalResolutionReasons,
  isAutoIgnoreReasonDescription,
  isProjectScope,
  isReleaseGroupScope,
} from '../../../shared/issuesV2';
import Locator from '../../../shared/Locator';
import { Action } from '../../../typings/permissions';
import { getProjectWithTheLatestIgnoredAt, ignoredAtFromProjects } from '../../helpers/issuesV2';
import { userHasPermissionForProject } from '../../helpers/permissions';
import { getHumanReadableEPSS, ignoreOrResolveWordPast } from '../../utils';
import * as AsyncValue from '../asyncValue';
import TeamsModule from '../teams';
import type {
  AnySort,
  APIIssueSummaryQuery,
  CategoriedIssuesFilterQuery,
  CategoriedIssuesQuery,
  CommonFilter,
  Issue,
  IssueRevision,
  LicensingIssue,
  ProjectScope,
  QualityIssue,
  Source,
  VulnFilter,
  VulnIssue,
} from '../../../shared/issuesV2';
import type { ReduxStore } from '../store';

import * as Selection from './selectionUtils';
import { byGrouping, getIssuesFromStore, groupLicensesByLicenseGroup } from './utils';
import type { HydrateStateQuery, IssueList, IssueRevisionWithIssues, IssuesV2Store } from './types';

//  Helper for grabbing the projectId used for fetching comments on an issue.
const getCommentsProjectId = (sourceId: Source['id']) => (sourceId ? Locator.parsePackageString(sourceId) : '');
export const getIssuesV2Store = (state: ReduxStore) => state.issuesV2;
const createIssuesV2Selector = <R>(selectorFn: (state: IssuesV2Store<Category>) => R) =>
  createSelector(getIssuesV2Store, selectorFn);

//  asyncStatus is divided into sub-fields for each of the various network requests.
export const getAsyncStatus = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.asyncStatus);
export const getAsyncStatusFetchIssues = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.fetchIssues);
export const getAsyncStatusCategorySummary = createSelector(
  getAsyncStatus,
  (asyncStatus) => asyncStatus.categorySummary,
);
export const getAsyncStatusStatusSummary = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.statusSummary);
export const getAsyncStatusUpdateIssues = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.updateIssues);
export const getAsyncStatusIssueDetails = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.issueDetails);
export const getAsyncStatusIssueExceptions = createSelector(
  getAsyncStatus,
  (asyncStatus) => asyncStatus.issueExceptions,
);
export const getAsyncStatusVulnDispute = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.vulnDispute);
export const getAsyncStatusIssueLicenses = createSelector(getAsyncStatus, (asyncStatus) => asyncStatus.issueLicenses);
export const getAsyncStatusExtendException = createSelector(
  getAsyncStatus,
  (asyncStatus) => asyncStatus.extendIssueException,
);

export const getIssueCategory = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.issueCategory);
export const getItems = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.items);
export const getIssues = createIssuesV2Selector(getIssuesFromStore);
export const getRevisions = createIssuesV2Selector((state: IssuesV2Store<Category>) =>
  state.items.grouping === 'revision' ? state.items.revisions : AsyncValue.resolve([]),
);
export const getCategorySummary = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.categorySummary);
export const getStatusSummary = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.statusSummary);
export const getIssueDetails = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.issueDetails);
export const getIsPackageCorrected = createIssuesV2Selector(
  (state: IssuesV2Store<Category>) => state.issueDetails?.source.isCorrected,
);
export const getContainerLayer = createSelector([getIssueDetails], (issueDetails): string | null => {
  const containerLayer = [];
  if ((issueDetails?.containerLayers?.base ?? 0) > 0) {
    containerLayer.push(`Base (${issueDetails?.containerLayers?.base})`);
  }
  if ((issueDetails?.containerLayers?.other ?? 0) > 0) {
    containerLayer.push(`Other (${issueDetails?.containerLayers?.other})`);
  }

  return containerLayer.length > 0 ? containerLayer.join(' and ') : null;
});
export const getIsLoadingIssueDetails = createIssuesV2Selector(
  (state: IssuesV2Store<Category>) => state.asyncStatus.issueDetails.isLoading,
);
export const getIsIssueDetailsUnlicensed = createSelector([getIssueDetails], (issueDetails) => {
  return issueDetails?.type === 'unlicensed_dependency';
});
export const getIsIssueDetailsUnconcluded = createSelector([getIssueDetails], (issueDetails) => {
  return issueDetails?.type === 'unconcluded_dependency';
});
export const getIsIssueDetailsMultiLicenseConcluded = createSelector([getIssueDetails], (issueDetails) => {
  return issueDetails?.type === 'multi_license_concluded';
});
export const getQualityIssueType = createSelector(
  [getIssueDetails],
  (issueDetails) => (issueDetails as QualityIssue)?.type,
);

export const getIssueLicenses = createIssuesV2Selector((state: IssuesV2Store<Category>) =>
  groupLicensesByLicenseGroup(state.licenses || []),
);
export const getDeclaredIssueLicenses = createSelector([getIssueLicenses], (licenses) =>
  licenses.filter((license) => license.isDeclared),
);
export const getDiscoveredIssueLicenses = createSelector([getIssueLicenses], (licenses) =>
  licenses.filter((license) => !license.isDeclared),
);
export const getIssueLicensesAsyncStatus = createIssuesV2Selector(
  (state: IssuesV2Store<Category>) => state.asyncStatus.issueLicenses,
);
export const getIssueLicenseCount = createSelector([getIssueLicenses], (issueLicences) => issueLicences?.length ?? 0);

export const getIssueLicenseList = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.issueLicenseList);

export const getIssuePackageRevisionLocator = createSelector(
  [getIssueDetails],
  (issueDetails) => issueDetails?.source?.id,
);
export const getIssuePackageTitle = createSelector([getIssueDetails], (issueDetails) => issueDetails?.source?.name);
const getLicensingIssueReason = createSelector([getIssueDetails], (issueDetails) => {
  const licensingIssue = issueDetails as LicensingIssue;
  if (licensingIssue?.type === 'policy_conflict') {
    return 'This license is denied by your licensing policy.';
  }
  if (licensingIssue?.type === 'policy_flag') {
    return 'This license is flagged by your licensing policy.';
  }
  if (licensingIssue?.type === 'unlicensed_dependency') {
    return 'This package is unlicensed.';
  }
  if (licensingIssue?.type === 'unconcluded_dependency') {
    return 'This package has identified licenses but does not have any marked as concluded.';
  }
  if (licensingIssue?.type === 'multi_license_concluded') {
    let multiLicenseConcluded = `More than one license was concluded for this dependency. Please review and ensure the correct license is in effect.`;
    if (licensingIssue.statuses.active > licensingIssue.statuses.ignored) {
      multiLicenseConcluded += ` You can ignore this issue if multiple licenses are accurate.`;
    }
    return multiLicenseConcluded;
  }

  return null;
});
const getIssuePackageDepthMessages = createSelector([getIssueDetails], (issueDetails) => {
  const licensingIssue = issueDetails as LicensingIssue;
  if (!licensingIssue) {
    return null;
  }
  const depthMessages: string[] = [];
  const { direct, deep } = licensingIssue.depths;
  if (direct === 1) {
    depthMessages.push('direct dependency');
  }
  if (deep === 1) {
    depthMessages.push('transitive dependency');
  }
  if (direct > 1) {
    depthMessages.push(`direct dependency of ${direct} projects`);
  }
  if (deep > 1) {
    depthMessages.push(`transitive dependency of ${deep} projects`);
  }

  return depthMessages;
});
const getLicensingIssueNotes = createSelector([getIssueDetails], (issueDetails) => {
  const licensingIssue = issueDetails as LicensingIssue;
  if (!licensingIssue) {
    return null;
  }

  return licensingIssue.details || null;
});
export const getLicensingIssueDetails = createSelector(
  [getIssueDetails, getLicensingIssueReason, getIssuePackageDepthMessages, getLicensingIssueNotes],
  (issueDetails, reason, depthMessages, notes) => {
    if (!issueDetails) {
      return null;
    }

    return {
      reason,
      depthMessages,
      notes,
    };
  },
);
export const getQualityIssueDetails = createSelector(
  [getIssueDetails, getIssuePackageDepthMessages],
  (issueDetails, depthMessages) => {
    if (!issueDetails) {
      return null;
    }

    return {
      type: issueDetails.type,
      sourceId: issueDetails.source.id,
      depthMessages,
      malwareRevisionId: issueDetails.malwareRevisionId,
    };
  },
);
export const getMetrics = createSelector(
  [getIssueDetails],
  (issueDetails) => (issueDetails as VulnIssue)?.metrics || [],
);
export const getActiveIssueId = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.activeIssueId);
export const getActiveIssue = createSelector([getIssues, getActiveIssueId], (issues: IssueList, activeIssueId) =>
  (issues as Issue[]).find((issue) => issue.id === activeIssueId),
);
export const getVulnDetails = createSelector([getIssueDetails], (activeIssue) => {
  if (!activeIssue) {
    return null;
  }
  const vulnIssue = activeIssue as VulnIssue;

  return {
    cve: vulnIssue.cve,
    cwes: vulnIssue.cwes,
    affectedVersionRanges: vulnIssue.affectedVersionRanges || [],
    patchedVersionRanges: vulnIssue.patchedVersionRanges || [],
    published: vulnIssue.published,
    cveStatus: vulnIssue.cveStatus || '',
    epss: vulnIssue.epss ? getHumanReadableEPSS(vulnIssue.epss).label : null,
  };
});
export const getIssueDetailsSourceLocatorParts = createSelector([getIssueDetails], (issueDetails) => {
  return !issueDetails ? null : Locator.parse(issueDetails.source.id);
});
export const getIsLinuxDep = createSelector([getIssueDetails], (issueDetails) => {
  return isLinuxFetcher(issueDetails?.source?.packageManager);
});
export const getIsCsBinaryDep = createSelector([getIssueDetails], (issueDetails) => {
  return issueDetails?.source?.packageManager === CodeSentryFetcherName;
});
export const getIssueCreatedAt = createSelector([getIssueDetails], (issueDetails) => issueDetails?.createdAt);
const getFirstProject = createSelector([getIssueDetails], (issueDetails) => issueDetails?.projects[0]);
// check if issue is active in the project scope
export const getIsIssueActive = createSelector([getFirstProject], (project) => {
  return project?.status === 'active';
});
// check if issue is ignored in the project scope
export const getIsIssueIgnored = createSelector([getFirstProject], (project) => {
  return project?.status === 'ignored';
});
export const getIssueDetailsJiraTickets = createSelector([getIssueDetails], (issueDetails) => {
  return issueDetails?.jiraTickets || [];
});
export const getFirstJiraTicket = createSelector([getIssueDetailsJiraTickets], (jiraTickets) => {
  return _.head(jiraTickets);
});

// check if issue is ticketed in the project scope
export const getIsIssueTicketed = createSelector([getIssueDetailsJiraTickets], (jiraTickets) => {
  return jiraTickets.length > 0;
});
export const getIssueIgnoredBy = createSelector([getFirstProject], (project) => project?.ignoredBy);
export const getIssueIgnoredAt = createSelector([getFirstProject], (project) => project?.ignoredAt);
export const getActiveIssueCount = createSelector(
  [getIssueDetails],
  (issueDetails) => issueDetails?.statuses.active || 0,
);
export const getIgnoredIssueCount = createSelector(
  [getIssueDetails],
  (issueDetails) => issueDetails?.statuses.ignored || 0,
);
//  Issue Details works a bit different in that we look at the `statuses` to determine if this
//  project issue (which is always just one) is either active or ignored.
export const getIssueDetailsIssueStatus = createSelector(
  [getActiveIssueCount, getIgnoredIssueCount],
  (activeCount, ignoredCount) => (ignoredCount > activeCount ? 'ignored' : 'active'),
);
export const getIssueCommentsProjectId = createSelector(
  [getActiveIssue, getIssueDetails],
  (activeIssue, issueDetails) => {
    const issue = issueDetails || activeIssue;
    //  We are calling `.trim` here for the rare case that no locator parts can be found
    //  but there are still spaces returned between each of the locator parts (see `parsePackageString`)
    return !issue ? '' : getCommentsProjectId(issue.source.id).trim();
  },
);
export const getIssueDetailsProjects = createSelector([getIssueDetails], (issueDetails) => {
  return issueDetails?.projects || [];
});
export const getIssueDetailsFirstProject = createSelector([getIssueDetailsProjects], (issueDetailsProjects) => {
  return _.head(issueDetailsProjects);
});

export const getStatus = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.status);
export const getScope = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.scope);
export const getSearch = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.search);
export const getFilter = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.filter);
export const getSort = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.sort);
export const getGrouping = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.items.grouping);
export const getPage = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.page);
export const getCount = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.count);
export const getProjectType = createIssuesV2Selector((state: IssuesV2Store<Category>) => state.projectType);
export const getIssueExceptions = createIssuesV2Selector(
  (state: IssuesV2Store<Category>) => state.issueExceptions.exceptions,
);
export const getIssueExceptionsCount = createIssuesV2Selector(
  (state: IssuesV2Store<Category>) => state.issueExceptions.total,
);

const getUnsortedAffectedProjects = createIssuesV2Selector(
  (state: IssuesV2Store<Category>) => state.affectedProjects || [],
);
const getActiveAffectedProjects = createSelector([getUnsortedAffectedProjects], (affectedProjects) => {
  return affectedProjects.filter((affectedProject) => affectedProject.status === 'active');
});
const getIgnoredAffectedProjects = createSelector([getUnsortedAffectedProjects], (affectedProjects) => {
  return affectedProjects.filter((affectedProject) => affectedProject.status === 'ignored');
});
export const getAffectedProjects = createSelector(
  [getActiveAffectedProjects, getIgnoredAffectedProjects],
  (activeProjects, ignoredProjects) => {
    //  We split them out like this because we always want to display active projects first.
    return [...activeProjects, ...ignoredProjects];
  },
);
export const getAffectedProjectsCount = createSelector(
  [getAffectedProjects],
  (activeProjects) => activeProjects.length,
);

export const getTicketsFromAffectedProjects = createSelector([getUnsortedAffectedProjects], (affectedProjects) => {
  return affectedProjects?.filter((project) => project.type) ?? [];
});
export const getTicketedIssueCount = createSelector([getTicketsFromAffectedProjects], (tickets) => {
  return tickets.length ?? 0;
});
//  Same as above, but only counts unique tickets (this gives us the correct count of unique Jira tickets).
export const getUniqueTicketedIssueCount = createSelector([getTicketsFromAffectedProjects], (tickets) => {
  const ticketSet = new Set(tickets);
  return ticketSet.size;
});

export const getAffectedProjectsAsyncStatus = createIssuesV2Selector((state) => state.asyncStatus.affectedProjects);
export const getIsIssueResolved = createSelector([getIssueDetails, getScope], (issueDetails, scanScope) => {
  if (!issueDetails) {
    return null;
  }
  if (scanScope.type === 'global') {
    return issueDetails.projects.every((project) => project.status === 'ignored');
  }

  return issueDetails.projects.some((project) => project.status === 'ignored');
});

/**
 * Returns an ignored message based on the active issue and scope.
 * Provides time-stamped message with project titles (up to 10) for global scope.
 * Returns a generic time-stamped message for project and release group scope.
 */
export const getIgnoredMessage = createSelector([getIssueDetails, getScope], (issueDetails, scanScope) => {
  if (!issueDetails) {
    return null;
  }
  const resolveMessageParts = [`Issue ${ignoreOrResolveWordPast().toLowerCase()}`];
  const earliestIgnoredAt = ignoredAtFromProjects(issueDetails.projects);
  if (earliestIgnoredAt) {
    resolveMessageParts.push(`${moment(earliestIgnoredAt).fromNow()}`);
  }
  const ignoredProjectTitle = issueDetails.projects
    .filter((issueProject) => issueProject.status === 'ignored')
    .map((issueProject) => issueProject.title);
  if (scanScope.type === 'global' && ignoredProjectTitle.length > 0) {
    resolveMessageParts.push(`in ${ignoredProjectTitle.slice(0, 10).join(', ')}`);
    if (ignoredProjectTitle.length > 10) {
      resolveMessageParts.push(`and ${ignoredProjectTitle.length - 10} more projects`);
    }
  }

  return resolveMessageParts.join(' ');
});

const getLatestResolvedIssueProject = createSelector([getIssueDetails], (issueDetails) => {
  if (!issueDetails) {
    return null;
  }
  const projects = issueDetails.projects;

  return getProjectWithTheLatestIgnoredAt(projects);
});

/**
 * Returns resolution reason of the latest ignored issue in project, or 'Other:' if no specific reason.
 * For non-vulnerability issues, it returns an empty string.
 */
export const getResolutionReason = createSelector(
  [getIssueCategory, getLatestResolvedIssueProject, getIssueDetails, getAffectedProjects],
  (category, project, issueDetails, affectedProjects) => {
    if (category === Category.Vulnerability) {
      return project?.ignoredReason ? `${project?.ignoredReason}: ` : 'Other: ';
    }

    if (
      category === Category.Licensing &&
      project?.ignoredReason &&
      isAutoIgnoreReasonDescription(project?.ignoredReason)
    ) {
      // Non-concluded license auto-ignores have a dynamic message based on the concluded license and dependency.
      if (project?.ignoredReason === InternalResolutionReasonDescriptions[InternalResolutionReasons.NonConcluded]) {
        // Concluded licenses live in the affected projects array, so we need to find the one that matches the issue's project.
        const concludedLicenses = affectedProjects.find(
          (affectedProject) => affectedProject.id === project.id,
        )?.concludedLicenses;
        const issueTitle = issueDetails?.source?.name;
        const issueRevision = issueTitle ? issueDetails?.source?.version : undefined;
        const numLicenses = concludedLicenses?.length ?? 0;
        const licenseList = concludedLicenses?.join(', ');
        return `This issue was automatically ignored because ${plural(numLicenses, 'a different license', 'different licenses')}${` (${licenseList})`} ${plural(numLicenses, 'has', 'have')} been concluded for ${issueTitle ? `${issueTitle} ` : 'this dependency'}${issueRevision || ''}`;
      }
      // All other auto-ignores have a static message.
      return `${project?.ignoredReason}${project?.ignoredNote ? ': ' : ''}: `;
    }
    return '';
  },
);

/**
 * Returns resolution notes of latest ignored issue in project, or 'No notes provided' if none exist.
 */
export const getResolutionNotes = createSelector(
  [getLatestResolvedIssueProject],
  (project) => {
    // If the user has provided a note, we always want to show it.
    if (project?.ignoredNote) {
      return project.ignoredNote;
    }

    // If the issue was auto-ignored, there will never be a note, and we shouldn't show a placeholder.
    if (project?.ignoredReason && isAutoIgnoreReasonDescription(project.ignoredReason)) {
      return '';
    }

    return 'No notes provided.';
  },
);

//  Returns the scan ID from the scope, if on projects or release groups (and it exists, as is optional/defaults to latest scan).
export const getScanIdFromScope = createSelector(
  [getScope],
  (scope): { revisionScanId: number } | { releaseScanId: number } | Record<string, unknown> => {
    if (isProjectScope(scope)) {
      return scope?.revisionScanId ? { revisionScanId: scope?.revisionScanId } : {};
    }
    if (isReleaseGroupScope(scope)) {
      return scope?.releaseScanId ? { releaseScanId: scope?.releaseScanId } : {};
    }
    return {};
  },
);

export const getIssueDetailsURL = createSelector(
  [getScanIdFromScope, (_state: ReduxStore, issueId: number) => issueId],
  (query, issueId) => {
    return `${window.location.pathname}/${issueId}${qs.stringify(query, { addQueryPrefix: true })}`;
  },
);

//  Open the issue drawer when there is a activeIssueId
export const getIsIssueDetailsDrawerOpen = createIssuesV2Selector((state) => state.activeIssueId !== null);

//  Pulls in the scan info and an issue ID and returns the issue details URL.
export const getIssueDetailsURLFromActiveIssueDetails = createSelector(
  [getIssueDetails, getIsIssueDetailsDrawerOpen],
  (issueDetails, isDrawerOpen) => {
    if (!issueDetails || !isDrawerOpen) {
      return window.location.pathname;
    }
    return issueDetails.url;
  },
);

// Whether search or any filter is applied
export const getHasSearchOrFilterApplied = createSelector([getFilter, getSearch], (filters, search): boolean => {
  // returns true if any of the values in `filters` have an object with at least one key, or if search, group or sort is truthy
  return Object.values(filters || {}).some((filter) => ((filter || []) as unknown[]).length > 0) || !!search;
});

//  Note: we can't import this from `../savedFilters`, as it would result in a circular dependency.
//  TODO: find a way to fix this?
const getSavedFiltersCurrentFilterId = createSelector(
  [(state: ReduxStore) => state.savedFilters.currentFilter],
  (currentFilter) => currentFilter || undefined,
);

//  This grabs the current state so that it can be merged with the query params
export const getHydrateStateQuery = createSelector(
  [
    getPage,
    getCount,
    getSort,
    getGrouping,
    getSearch,
    getStatus,
    getFilter,
    getSavedFiltersCurrentFilterId,
    getScanIdFromScope,
    getScope,
  ],
  (
    page,
    count,
    sort,
    grouping,
    search,
    status,
    filter,
    savedFilter,
    scanId,
    scope,
  ): HydrateStateQuery => ({
    page,
    count,
    sort,
    grouping,
    search,
    status,
    filter,
    savedFilter,
    scope: {
      compareTo: {
        changeStatus: (scope as ProjectScope).compareTo?.changeStatus,
      },
    },
    ...scanId,
  }),
);

/**
 * Maps the state to a valid IssuesFilterQuery.
 * This is what informs the backend of the current state of the FE's filters,
 */
export const getIssuesFilterQuery = createSelector(
  [getIssueCategory, getStatus, getScope, getSearch, getFilter, TeamsModule.selectors.getTeamSelection],
  (category, status, scope, search, filter, teamSelection): CategoriedIssuesFilterQuery<typeof category> =>
    ({
      category,
      status,
      scope,
      teamId: teamSelection,
      filter: {
        ...filter,
        search,
        foundAfter: dateForFoundAfterOption(filter.foundAfter),
      },
    }) as CategoriedIssuesFilterQuery<typeof category>,
);

/**
 * Maps the state to a valid APIIssuesQuery.
 * This is what informs the backend of the current state of the FE,
 * ie: which filters/sorting/pagination are in place.
 */
export const getFetchIssuesQuery = createSelector(
  [getIssuesFilterQuery, getSort, getPage, getCount],
  (
    query: CategoriedIssuesFilterQuery<Category>,
    sort: AnySort,
    page: number,
    count: number,
  ): CategoriedIssuesQuery<Category> => ({
    ...query,
    sort,
    page,
    count,
  }),
);

/**
 * Maps the state to a valid APIIssueSummaryQuery.
 */
export const getIssuesSummaryQuery = createSelector(
  [getIssuesFilterQuery],
  <C extends Category>(query: CategoriedIssuesFilterQuery<C>): APIIssueSummaryQuery<C> =>
    _.omit(query, 'status') as APIIssueSummaryQuery<C>,
);

//  Returns either 'all' or an array of ids of the selected issues.
export const getSelectedIssues = createIssuesV2Selector(Selection.getSelectedIssues);
export const getSelectedIssueIds = createSelector(getSelectedIssues, (issues) =>
  issues === 'all' ? 'all' : issues.map((i) => i.id),
);

//  Returns either 'all' or an array of ids of the selected revisions.
export const getSelectedRevisions = createIssuesV2Selector(Selection.getSelectedRevisions);
export const getSelectedRevisionIds = createSelector(getSelectedRevisions, (revisions) =>
  revisions === 'all' ? 'all' : revisions.map((r) => r.revisionId),
);

/**
 * Return the issues filter query that corresponds to the current set of
 * filters AND currently selected issues / revisions.
 */
export const getSelectedIssuesFilterQuery = createSelector(
  [getIssuesFilterQuery, getSelectedIssueIds, getSelectedRevisionIds],
  (query: CategoriedIssuesFilterQuery<Category>, selectedIssueIds, selectedRevisionIds) => {
    const selectedIssuesQuery = { ...query };
    if (selectedIssueIds !== 'all') {
      selectedIssuesQuery.ids = selectedIssueIds;
    }
    if (selectedRevisionIds !== 'all') {
      selectedIssuesQuery.filter = { ...selectedIssuesQuery.filter, revisionIds: selectedRevisionIds };
    }
    return selectedIssuesQuery;
  },
);

//  Gets the total number of issues / revisions based on the filters, which we store in the statusSummary.
export const getTotalNumberOfItems = createSelector(
  [getGrouping, getStatus, getStatusSummary],
  (grouping, status, statusSummary): number | null => {
    //  While there is no status summary, just return null and we won't render the pagination.
    if (!statusSummary) {
      return null;
    }

    if (grouping === 'revision') {
      return statusSummary.revisions[status];
    }

    return statusSummary.issues[status];
  },
);

// Gets the total number of issues for the bulk selection banner
export const getTotalIssuesForBanner = createSelector(
  [getStatus, getStatusSummary],
  (status, statusSummary): number | null => {
    if (!statusSummary) {
      return null;
    }

    return statusSummary.issues[status];
  },
);

//  Used to tally the total number of issues, used in the project summary dropdown.
export const getTotalNumberOfIssuesFromSummary = createSelector(
  [getAsyncStatusCategorySummary, getCategorySummary],
  (status, categorySummary): number | null =>
    status.loading || !categorySummary ? null : Object.values(categorySummary).reduce((total, count) => total + count),
);

// Takes an issue and returns whether it is selected or not.
export const getIsIssueSelected = (issue: Issue) =>
  createIssuesV2Selector((state) => Selection.isIssueSelected(state, issue));

// Takes a revision and returns whether it is selected or not.
export const getIsRevisionSelected = (revision: IssueRevision) =>
  createIssuesV2Selector((state) => Selection.isRevisionSelected(state, revision));

// Takes a revision and returns whether any issue for that revision is selected or not.
export const getIsAnyIssueForRevisionSelected = (revision: IssueRevision) =>
  createIssuesV2Selector((state) => Selection.isAnyIssueForRevisionSelected(state, revision));

// Returns whether every issue across all pages are selected.
export const getAreAllIssuesSelected = createIssuesV2Selector(Selection.areAllIssuesSelected);

// Returns the number of selected issues across both issue and revision selections.
export const getSelectedIssuesCount = createIssuesV2Selector(Selection.countSelectedIssues);

// Selector which tells us if only SOME issues are selected.
export const getAreSomeIssuesSelected = createIssuesV2Selector(Selection.isAnyIssueOnPageSelected);

//  Selector which tells us if every issue on the current page is selected.
export const getIsEveryIssueOnPageSelected = createIssuesV2Selector(Selection.isEveryIssueOnPageSelected);

//  Selector for IssuesSelectAllCheckbox state.
export const getBulkCheckboxState = createSelector(
  [getIsEveryIssueOnPageSelected, getAreSomeIssuesSelected],
  (isEveryIssueSelected, someIssuesSelected): 'empty' | 'indeterminate' | 'checked' => {
    if (isEveryIssueSelected) {
      return 'checked';
    }
    if (someIssuesSelected) {
      return 'indeterminate';
    }
    return 'empty';
  },
);

//  Used in the BulkSelectionBanner to determine how many issues are actually on the page.
//  Helps avoid bugs when there are less issues on the page then the count.
export const getIssuesOnPageCount = createIssuesV2Selector((state) =>
  byGrouping(
    state,
    ({ issues }) => issues.length,
    ({ revisions }) =>
      (revisions.value as IssueRevision[])?.reduce((acc, revision) => acc + revision.issueCount, 0) ?? 0,
  ),
);

//  Selector which allows us to pluralize the "Unlink ticket(s)" button.
export const getJiraTicketCountForSelectedIssues = createSelector(
  [getGrouping, getSelectedIssues, getIssues, getSelectedRevisions, getRevisions],
  (grouping, selectedIssues, allIssues, selectedRevisions, allRevisions) => {
    //  Get all the selected issues
    const issues: Issue[] = selectedIssues === 'all' ? allIssues : selectedIssues;
    //  Count all jiraTickets for each selected issue.
    const ticketCountFromIssues = issues.flatMap((issue) => issue.jiraTickets ?? []).length;

    //  If ungrouped, then we only care about counts from selected issues
    if (grouping === 'issue') {
      return ticketCountFromIssues;
    }

    //  Otherwise, we need to ALSO include the ticket counts from selected revisions
    const revisions = selectedRevisions === 'all' ? (allRevisions.value ?? []) : selectedRevisions;
    const ticketCountFromRevisions = _.sumBy(revisions, (revision) => revision.jiraTicketCount);
    return ticketCountFromIssues + ticketCountFromRevisions;
  },
);

/** Selects whether issues are currently loading or not. */
export const getIsLoadingIssues = createIssuesV2Selector((state) =>
  byGrouping(
    state,
    () => state.asyncStatus.fetchIssues.isLoading,
    ({ revisions }) => revisions.isLoading,
  ),
);

/** Triggers generating a CSV file for selected issues */
export const getGenerateSelectedIssuesUrl = createSelector([getSelectedIssuesFilterQuery], (selectedIssuesQuery) => {
  const params = qs.stringify({
    ...selectedIssuesQuery,
    csv: true,
    email: true,
    includeDirectDependencyOriginPaths: true,
  });
  return `${ENDPOINTS.GENERATE_ISSUES}?${params}`;
});

export const isRevisionExpanded = (revisionId: string) =>
  createIssuesV2Selector(
    (state) =>
      (state.items.grouping === 'revision' &&
        state.items.revisions.value &&
        (state.items.revisions.value as Array<IssueRevisionWithIssues<unknown>>).find(
          (r) => r.revisionId === revisionId,
        )?.isExpanded) ??
      false,
  );

export const isAnyRevisionExpanded = createSelector(
  getRevisions,
  (revisions) => revisions.value?.some((revision) => revision.isExpanded) ?? false,
);

export const getIssueTypeSummary = createIssuesV2Selector((state) => state.typeSummary);
export const getSortedIssueTypeSummary = createSelector([getIssueTypeSummary], (typeSummaryAsyncValue) =>
  AsyncValue.orNull(typeSummaryAsyncValue, (typeSummary) =>
    Object.entries(typeSummary)
      //  Remove counts of 0
      .filter(([, count]) => count > 0)
      //  Sort in descending order by count.
      .sort(([, countA], [, countB]) => countB - countA),
  ),
);
export const getTotalIssuesFromTypeSummary = createSelector([getIssueTypeSummary], (typeSummaryAsyncValue) =>
  AsyncValue.orNull(typeSummaryAsyncValue, (typeSummary) =>
    Object.values(typeSummary).reduce((acc, count) => acc + count),
  ),
);

export const getPackageManagers = createIssuesV2Selector((state) => state.packageManagers);
export const getPackageManagersFilter = createSelector(
  [getFilter],
  (filter: CommonFilter) => filter.packageManagers ?? [],
);

export const getCwes = createIssuesV2Selector((state) => state.cwes);
export const getCweFilter = createSelector([getFilter], (filter: VulnFilter) => filter.cwes ?? []);

export const getProjectLabelsFilter = createSelector([getFilter], (filter: CommonFilter) => filter.projectLabels ?? []);

//  Determines if the user has the ability to edit the source package for this project.
export const getCanUserEditSourceProject = createSelector([getIssueDetails], (issueDetails) => {
  if (!issueDetails) {
    return false;
  }
  const { packageManager, name } = issueDetails.source;
  return userHasPermissionForProject(Action.Edit, `${packageManager}+${name}`);
});

export const getProjectLocatorFromScope = createSelector([getScope], (scope) => {
  if (isProjectScope(scope)) {
    return `${scope.id}$${scope.revision}`;
  }
  return undefined;
});

export const getComparisonMode = createIssuesV2Selector((state) => state.comparisonMode);

export const getChangeStatus = createSelector([getScope], (scope) => {
  if (isProjectScope(scope)) {
    return scope.compareTo?.changeStatus;
  }
  return undefined;
});
